import { Amplify, Auth } from 'aws-amplify';
import axios from 'axios';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';

import AwsConfigAuth from '../aws-config/auth';
import { throwError, formatMobileNumberJapaneseToCountryCode } from 'utils';

Amplify.configure({ Auth: AwsConfigAuth });

export interface AccountSession {
  valid: boolean;
  token?: string;
}

interface UseAuth {
  isLoading: boolean;
  waitingForVerificationCodeInput: boolean;
  waitingForMFAVerificationCodeInput: boolean;
  waitingForEmailAttributeVerificationCodeInput: boolean;
  waitingForPhoneNumberAttributeVerificationCodeInput: boolean;
  isAuthenticated: boolean;
  isSetMFA: boolean;
  email: string;
  phoneNumber: string;
  user: any;
  username: string;
  companyId: number;
  factoryId: number;
  userId: number;
  token: string;
  authorizationHeader: { Authorization?: string };
  signUp: (email: string, password: string) => Promise<Result>;
  forgotPassword: (email: string) => Promise<Result>;
  forgotPasswordSubmit: (verificationCode: string, newPassword: string) => Promise<Result>;
  createUser: (email: string, password: string, name: string, display_id: string) => Promise<Result>;
  confirmSignUp: (verificationCode: string) => Promise<Result>;
  confirmMFA: (verificationCode: string) => Promise<Result>;
  confirmEmailAttributeSubmit: (verificationCode: string) => Promise<Result>;
  confirmPhoneNumberAttributeSubmit: (verificationCode: string) => Promise<Result>;
  signIn: (email: string, password: string) => Promise<Result>;
  changePassword: (oldPassword: string, password: string) => Promise<Result>;
  updateEmail: (email: string) => Promise<Result>;
  submitPhoneNumber: (phoneNumber: string) => Promise<Result>;
  disableMFA: () => Promise<Result>;
  signOut: () => void;
  cancelEmail: () => void;
  cancelMFA: () => void;
  deleteUser: () => Promise<Result>;
  currentSession: () => Promise<AccountSession>;
}

interface Result {
  success: boolean;
  message: string;
}

const authContext = createContext({} as UseAuth);

type MyComponentProps = {
  children: React.ReactNode;
};
export const ProvideAuth: React.FC<MyComponentProps> = (props) => {
  const auth = useProvideAuth();
  return <authContext.Provider value={auth}>{props.children}</authContext.Provider>;
};

export const useAuth = () => {
  return useContext(authContext);
};

const useProvideAuth = (): UseAuth => {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isSetMFA, setIsSetMFA] = useState(false);
  const [waitingForVerificationCodeInput, setWaitingForVerificationCodeInput] = useState(false);
  const [waitingForMFAVerificationCodeInput, setWaitingForMFAVerificationCodeInput] = useState(false);
  const [waitingForEmailAttributeVerificationCodeInput, setWaitingForEmailAttributeVerificationCodeInput] = useState(false);
  const [waitingForPhoneNumberAttributeVerificationCodeInput, setWaitingForPhoneNumberAttributeVerificationCodeInput] = useState(false);
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [companyId, setCompanyId] = useState(0);
  const [factoryId, setFactoryId] = useState(0);
  const [userId, setUserId] = useState(0);
  const [token, setToken] = useState('');
  const [authorizationHeader, setAuthorizationHeader] = useState({});
  const [user, setUser] = useState<any>(null);

  const apiTokenCookieName = 'apiToken';
  const [cookies, setCookie, removeCookie] = useCookies([apiTokenCookieName]);

  function makeAuthorizationHeader(token: string) {
    return { Authorization: `Bearer ${token}` };
  }

  useEffect(() => {
    Auth.currentAuthenticatedUser({ bypassCache: true }).then((result) => {
      try {
        setUsername(result.username);
        setEmail(result.attributes.email)
        setPhoneNumber(result.attributes.phone_number)
        const availableToken = token || cookies.apiToken
        if (availableToken) {
          setToken(availableToken);
        } else {
          throwError('TOKEN expired');
        }
        axios.get('/v0/users/verify', {
          headers: makeAuthorizationHeader(availableToken)
        }).then((res) => {
          const { company_id, factory_ids, id: user_id } = res.data;
          if (company_id && factory_ids && user_id) {
            setCompanyId(company_id);
            setFactoryId(factory_ids && factory_ids[0]);
            setUserId(user_id);
            setIsAuthenticated(true);
          } else {
            throwError('Invalid user data');
          }
        }).catch((err) => {
          throw err;
        }).finally(() => {
          setIsLoading(false);
        });
      } catch (err) {
        console.error(err);
        setUsername('');
        setEmail('');
        setPhoneNumber('');
        setIsAuthenticated(false);
        setIsLoading(false);
      }
    }).catch((err) => {
      console.log(err);
      setUsername('');
      setEmail('');
      setPhoneNumber('');
      setIsAuthenticated(false);
      setIsLoading(false);
    });
  }, []);

  useEffect(() => {
    setAuthorizationHeader(makeAuthorizationHeader(token));
    if (token) {
      setCookie(apiTokenCookieName, token, { path: '/', maxAge: 86400 });
    } else {
      removeCookie(apiTokenCookieName, { path: '/' });
    }
  }, [token]);

  // TODO: userに関するプロパティが散在しすぎなので１つの型にまとめる
  const submitAuthResult = async (result: any) => {
    try {
      const session = await currentSession()
      const res = await axios.post('/v0/users/auth', {
        cognito_username: result.username,
        email: result.email
      }, {
        headers: {
          'Authorization': `Bearer ${session.token}`,
          'Content-Type': 'application/json'
        }
      });
      const { company_id, factory_ids, id: user_id, token } = res.data;
      if (company_id && factory_ids && user_id) {
        setUsername(result.username);
        setEmail(result.email);
        setPhoneNumber(result.attributes.phone_number)
        setCompanyId(company_id);
        setFactoryId(factory_ids && factory_ids[0]);
        setUserId(user_id);
        setToken(token);
        setIsAuthenticated(true);
      } else {
        throwError('Invalid user data');
      }
    } catch (err) {
      throw err;
    }
  };

  const forgotPassword = async (email: string) => {
    try {
      await Auth.forgotPassword(email);
      setEmail(email);
      setWaitingForVerificationCodeInput(true);
      return { success: true, message: '認証コードが送信されました。認証コードを入れてください。' };
    } catch (error: any) {
      return {
        success: false,
        message: error,
      };
    }
  }

  const forgotPasswordSubmit = async (verificationCode: string, newPassword: string) => {
    try {
      await Auth.forgotPasswordSubmit(email, verificationCode, newPassword);
      await signIn(email, newPassword);
      setWaitingForVerificationCodeInput(false);
      return { success: true, message: '再設定が完了しました。' };
    } catch (error: any) {
      return {
        success: false,
        message: error,
      };
    }
  };

  const signUp = async (email: string, password: string) => {
    try {
      await Auth.signUp({
        username: email,
        password: password,
        attributes: {
          email: email,
        }
      });
      setIsAuthenticated(false);
      setEmail(email);
      setPassword(password);
      return { success: true, message: '' };
    } catch (error: any) {
      return {
        success: false,
        message: error,
      };
    }
  };

  const createUser = async (email: string, password: string, name: string, display_id: string) => {
    try {
      await axios.get(`/v0/account/user-check-duplicate`, {
        headers: authorizationHeader,
        params: { factory_id: factoryId, display_id: display_id }
      });
      const res = await Auth.signUp({
        username: email,
        password: password,
        attributes: {
          email: email,
        }
      });
      const branchData = {
        username: res.userSub,
        name: name,
        display_id: display_id,
        email: email
      }
      await axios.post(`/v0/account/users`, branchData, {
        headers: authorizationHeader,
        params: { factory_id: factoryId }
      });
      return {
        success: true,
        message: '作成成功'
      };
    } catch (error: any) {
      return {
        success: false,
        message: error,
      };
    }
  };

  const confirmSignUp = async (verificationCode: string) => {
    try {
      await Auth.confirmSignUp(email, verificationCode);
      const result = await signIn(email, password);
      setIsAuthenticated(true);
      setWaitingForVerificationCodeInput(false);
      setPassword('');
      return result;
    } catch (error: any) {
      return {
        success: false,
        message: error,
      };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const result = await Auth.signIn(email, password);
      if (result.challengeName === "SMS_MFA") {
        setIsSetMFA(true);
        setEmail(email);
        setPassword(password);
        setUser(result);
        setWaitingForMFAVerificationCodeInput(true);
        return {
          success: false,
          message: 'SMS認証コードを送りました。',
        };
      }
      await submitAuthResult({ ...result, email: email });
      return { success: true, message: '' };
    } catch (error: any) {
      if (error.code === "UserNotConfirmedException") {
        try {
          await Auth.resendSignUp(email);
          setEmail(email);
          setPassword(password);
          setWaitingForVerificationCodeInput(true);
          return {
            success: false,
            message: '初回ログインのため認証コードをメールに送りました。',
          };
        } catch (err) {
          return {
            success: false,
            message: '予期せぬエラーが発生しました。',
          };
        }
      } else {
        return {
          success: false,
          message: '認証に失敗しました。',
        };
      }
    }
  };

  const confirmMFA = async (verificationCode: string) => {
    try {
      const currentUser = await Auth.confirmSignIn(user, verificationCode);
      await submitAuthResult({ ...currentUser, email: email });
      setUser(currentUser);
      setWaitingForMFAVerificationCodeInput(false);
      setPassword('');
      return { success: true, message: '' };
    } catch (error: any) {
      return {
        success: false,
        message: error,
      };
    }
  }

  const signOut = async () => {
    try {
      await Auth.signOut();
      setEmail('');
      setIsAuthenticated(false);
      setToken('');
      return { success: true, message: '' };
    } catch (error) {
      return {
        success: false,
        message: 'ログアウトに失敗しました。',
      };
    }
  };

  const deleteUser = async () => {
    try {
      const res = await axios.get(`/v0/account/users`, {
        headers: authorizationHeader,
        params: { factory_id: factoryId }
      });
      if (res.data.length === 1) {
        return {
          success: false,
          message: 'ユーザーは一人以上登録している必要があります。',
        };
      }
      const user = await Auth.currentAuthenticatedUser();
      await Auth.deleteUser();
      await axios.delete(`/v0/account/users`, {
        headers: authorizationHeader,
        params: {
          factory_id: factoryId,
          username: user.username
        }
      });
      signOut();
      return {
        success: true,
        message: 'ユーザーを削除しました。',
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.message,
      };
    }
  };

  const currentSession = async (): Promise<AccountSession> => {
    try {
      const res = await Auth.currentSession();
      return { valid: true, token: res.getAccessToken().getJwtToken() };
    } catch (err) {
      throw { valid: false, msg: err };
    }
  };

  const changePassword = async (oldPassword: string, password: string) => {
    try {
      const user = await Auth.currentAuthenticatedUser();
      await Auth.changePassword(user, oldPassword, password);
      return {
        success: true,
        message: 'パスワードの変更に成功しました。',
      };
    } catch (error: any) {
      return {
        success: false,
        message: error,
      };
    }
  }

  const updateEmail = async (email: string) => {
    try {
      const user = await Auth.currentAuthenticatedUser();
      await Auth.updateUserAttributes(user, { email: email });
      setEmail(email);
      setWaitingForEmailAttributeVerificationCodeInput(true);
      return {
        success: true,
        message: '認証コードが送信されました。認証コードを入れてください。',
      };
    } catch (error: any) {
      return {
        success: false,
        message: error,
      };
    }
  }

  const cancelEmail = () => {
    setWaitingForEmailAttributeVerificationCodeInput(false);
    return {
      success: true,
      message: 'キャンセルされました',
    };
  }

  const cancelMFA = () => {
    setWaitingForPhoneNumberAttributeVerificationCodeInput(false);
    return {
      success: true,
      message: 'キャンセルされました',
    };
  }

  const confirmEmailAttributeSubmit = async (verificationCode: string) => {
    try {
      await Auth.verifyCurrentUserAttributeSubmit('email', verificationCode);
      setWaitingForEmailAttributeVerificationCodeInput(false);
      await axios.patch(`/v0/account/user`, { email: email, type: "email" }, { headers: authorizationHeader });
      setEmail(email);
      return {
        success: true,
        message: 'メールアドレスが変更されました。',
      };
    } catch (error: any) {
      return {
        success: false,
        message: error,
      };
    }
  };

  const submitPhoneNumber = async (phoneNumber: string) => {
    try {
      const user = await Auth.currentAuthenticatedUser();
      await Auth.updateUserAttributes(user, {
        'phone_number': formatMobileNumberJapaneseToCountryCode(phoneNumber)
      });
      await Auth.verifyCurrentUserAttribute('phone_number');
      setWaitingForPhoneNumberAttributeVerificationCodeInput(true);
      return {
        success: true,
        message: '認証コードが送信されました。認証コードを入れてください。',
      };
    } catch (error: any) {
      return {
        success: false,
        message: error,
      };
    }
  }

  const confirmPhoneNumberAttributeSubmit = async (verificationCode: string) => {
    const currentUser = await Auth.currentAuthenticatedUser();
    try {
      await Auth.verifyCurrentUserAttributeSubmit('phone_number', verificationCode);
      await Auth.setPreferredMFA(currentUser, 'SMS');
      setPhoneNumber(currentUser.attributes.phone_number);
      setIsSetMFA(true);
      setWaitingForPhoneNumberAttributeVerificationCodeInput(false);
      return {
        success: true,
        message: '電話番号の検証が完了し、SMS MFAが追加されました。',
      };
    } catch (error: any) {
      return {
        success: false,
        message: error,
      };
    }
  };

  const disableMFA = async () => {
    const currentUser = await Auth.currentAuthenticatedUser();
    try {
      await Auth.setPreferredMFA(currentUser, "NOMFA");
      await Auth.updateUserAttributes(currentUser, { phone_number: '' });
      setIsSetMFA(false);
      setPhoneNumber('');
      return {
        success: true,
        message: 'MFAを削除しました。',
      };
    } catch (error: any) {
      return {
        success: false,
        message: error,
      };
    }
  }

  // FIXME: デバッグ用（後で消す）
  useEffect(() => {
    console.log('UserIdentifier: ', companyId, factoryId, userId, token);
  }, [companyId, factoryId, userId, token]);

  return {
    isLoading,
    waitingForVerificationCodeInput,
    waitingForMFAVerificationCodeInput,
    waitingForEmailAttributeVerificationCodeInput,
    waitingForPhoneNumberAttributeVerificationCodeInput,
    isAuthenticated,
    isSetMFA,
    email,
    phoneNumber,
    username,
    companyId,
    factoryId,
    userId,
    user,
    token,
    authorizationHeader,
    signUp,
    forgotPassword,
    forgotPasswordSubmit,
    createUser,
    confirmSignUp,
    confirmMFA,
    confirmEmailAttributeSubmit,
    confirmPhoneNumberAttributeSubmit,
    signIn,
    signOut,
    deleteUser,
    currentSession,
    changePassword,
    updateEmail,
    cancelEmail,
    cancelMFA,
    submitPhoneNumber,
    disableMFA
  };
};
